type SoundPreset = { id: string; label: string; type: OscillatorType; frequencies: number[]; offsets: number[]; spacing: number; duration: number; assetUrl?: string };

export const ORDER_SOUND_PRESETS: SoundPreset[] = [
  { id: "kitchen", label: "Kitchen Call", type: "triangle", frequencies: [784, 988, 1175, 1568], offsets: [0, 1.2], spacing: 0.22, duration: 0.32 },
  { id: "doorbell", label: "Doorbell", type: "sine", frequencies: [880, 659], offsets: [0, 1.15], spacing: 0.42, duration: 0.65 },
  { id: "chime", label: "Bright Chime", type: "sine", frequencies: [1047, 1319, 1568], offsets: [0, 1.15], spacing: 0.2, duration: 0.65 },
  { id: "beep", label: "Double Beep", type: "triangle", frequencies: [1200, 1200], offsets: [0, 0.85, 1.7], spacing: 0.23, duration: 0.16 },
  { id: "urgent", label: "Urgent Pulse", type: "triangle", frequencies: [880, 1320, 880, 1320], offsets: [0, 1.15], spacing: 0.2, duration: 0.18 },
  { id: "order-received", label: "Order Received", type: "sine", frequencies: [], offsets: [], spacing: 0, duration: 0,
    assetUrl: `${process.env.PUBLIC_URL || ""}/sounds/new-order-received.wav` },
];

export const resolveOrderSound = (id: string | null) => ORDER_SOUND_PRESETS.find(p => p.id === id) || ORDER_SOUND_PRESETS[0];

export class InstantOrderSound {
  private context: AudioContext | null = null;
  private output: GainNode | null = null;
  private lastPlayed = -Infinity;
  private active = new Set<OscillatorNode>();
  private customBuffer: AudioBuffer | null = null;
  private customSource: AudioBufferSourceNode | null = null;
  private assetBuffers = new Map<string, Promise<AudioBuffer>>();

  async loadCustomSound(dataUrl: string) {
    if (!(await this.unlock()) || !this.context) throw new Error("Audio unavailable");
    const response = await fetch(dataUrl);
    const buffer = await this.context.decodeAudioData(await response.arrayBuffer());
    if (buffer.duration > 30) throw new Error("Choose a sound under 30 seconds");
    this.customBuffer = buffer;
  }

  private stopActive() {
    if (this.customSource) {
      try { this.customSource.stop(); } catch {}
      this.customSource.disconnect();
      this.customSource = null;
    }
    this.active.forEach(oscillator => { try { oscillator.stop(); } catch {} });
    this.active.clear();
  }

  async unlock(): Promise<boolean> {
    const Context = window.AudioContext || (window as any).webkitAudioContext;
    if (!Context) return false;
    try {
      if (!this.context || this.context.state === "closed") {
        this.context = new Context();
        this.lastPlayed = -Infinity;
        this.output = this.context!.createGain();
        const compressor = this.context!.createDynamicsCompressor();
        compressor.threshold.value = -6;
        compressor.knee.value = 12;
        compressor.ratio.value = 4;
        this.output.connect(compressor);
        compressor.connect(this.context!.destination);
      }
      if (this.context!.state === "suspended") await this.context!.resume();
      return this.context!.state === "running";
    } catch { return false; }
  }

  setVolume(volume: number) {
    if (this.output && this.context) this.output.gain.setValueAtTime(Math.max(0, Math.min(1, volume)), this.context.currentTime);
  }

  async play(volume: number, userGesture = false, presetId = "kitchen"): Promise<boolean> {
    if (userGesture && !(await this.unlock())) return false;
    if (!this.context || this.context.state !== "running" || !this.output) return false;
    if (presetId === "custom" && !this.customBuffer) return false;
    this.setVolume(volume);
    const preset = resolveOrderSound(presetId);
    let buffer = presetId === "custom" ? this.customBuffer : null;
    if (preset.assetUrl) {
      const context = this.context;
      let loading = this.assetBuffers.get(preset.assetUrl);
      if (!loading) {
        loading = fetch(preset.assetUrl).then(async response => {
          if (!response.ok) throw new Error("Could not load order sound");
          return context.decodeAudioData(await response.arrayBuffer());
        });
        this.assetBuffers.set(preset.assetUrl, loading);
      }
      try { buffer = await loading; }
      catch { this.assetBuffers.delete(preset.assetUrl); return false; }
      if (this.context !== context || context.state !== "running" || !this.output) return false;
    }
    const now = this.context.currentTime;
    if (!userGesture && now - this.lastPlayed < 2.4) return true;
    this.stopActive();
    this.lastPlayed = now;
    if (buffer) {
      const source = this.context.createBufferSource();
      source.buffer = buffer;
      source.connect(this.output);
      source.onended = () => { source.disconnect(); if (this.customSource === source) this.customSource = null; };
      this.customSource = source;
      source.start();
      return true;
    }
    for (const offset of preset.offsets) preset.frequencies.forEach((frequency, index) => {
      const start = now + offset + index * preset.spacing;
      const oscillator = this.context!.createOscillator();
      const envelope = this.context!.createGain();
      oscillator.type = preset.type;
      oscillator.frequency.setValueAtTime(frequency, start);
      envelope.gain.setValueAtTime(0.001, start);
      envelope.gain.linearRampToValueAtTime(0.7, start + 0.015);
      envelope.gain.setValueAtTime(0.7, start + 0.1);
      envelope.gain.exponentialRampToValueAtTime(0.001, start + preset.duration);
      oscillator.connect(envelope);
      envelope.connect(this.output!);
      this.active.add(oscillator);
      oscillator.onended = () => { this.active.delete(oscillator); oscillator.disconnect(); envelope.disconnect(); };
      oscillator.start(start);
      oscillator.stop(start + preset.duration + 0.02);
    });
    return true;
  }

  close() {
    this.stopActive();
    if (this.context && this.context.state !== "closed") void this.context.close().catch(() => {});
    this.context = null;
    this.output = null;
    this.customBuffer = null;
    this.assetBuffers.clear();
  }
}
